/**
 * Kodokyo Feedback Widget
 * Embeddable IIFE script for visual bug reporting
 *
 * Usage:
 *   KodokyoFeedback.init({
 *     widgetKey: '...',
 *     apiUrl: '...',
 *     user: { name: 'John', email: 'john@example.com' }  // optional auto-fill
 *   })
 */

import { captureQuick, capturePerfect, isPerfectCaptureAvailable } from "./capture";
import { createAnnotationUI } from "./annotate";
import { collectMetadata } from "./metadata";
import { submitFeedback, uploadAttachment } from "./api";

interface WidgetConfig {
  widgetKey: string;
  apiUrl: string;
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left" | "bottom-middle" | "top-middle" | "left-middle" | "right-middle";
  offsetX?: number;
  offsetY?: number;
  color?: string;
  textColor?: string;
  label?: string;
  borderRadius?: string;
  sessionReplay?: boolean;
  user?: {
    name?: string;
    email?: string;
  };
}

interface RemoteConfig {
  color: string;
  textColor: string;
  position: string;
  offsetX: number;
  offsetY: number;
  label: string;
  borderRadius: string;
}

let config: WidgetConfig | null = null;
let appearance: RemoteConfig | null = null;

async function fetchRemoteConfig(): Promise<RemoteConfig | null> {
  if (!config) return null;
  try {
    const res = await fetch(`${config.apiUrl}/config?widgetKey=${config.widgetKey}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function getPositionStyles(pos: string, offsetX: number, offsetY: number): { css: string; baseTransform: string } {
  if (pos === "bottom-middle") return { css: `bottom: ${offsetY}px; left: 50%;`, baseTransform: "translateX(-50%)" };
  if (pos === "top-middle")    return { css: `top: ${offsetY}px; left: 50%;`,    baseTransform: "translateX(-50%)" };
  if (pos === "left-middle")   return { css: `left: ${offsetX}px; top: 50%;`,    baseTransform: "translateY(-50%)" };
  if (pos === "right-middle")  return { css: `right: ${offsetX}px; top: 50%;`,   baseTransform: "translateY(-50%)" };
  const h = pos.includes("left") ? "left" : "right";
  const v = pos.includes("top") ? "top" : "bottom";
  return { css: `${h}: ${offsetX}px; ${v}: ${offsetY}px;`, baseTransform: "" };
}

function applyButtonStyles(button: HTMLButtonElement, cfg: RemoteConfig) {
  const icon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>`;
  const label = cfg.label;
  button.innerHTML = label ? `${icon}<span style="margin-left:6px;">${label}</span>` : icon;

  const pos = cfg.position || "bottom-right";
  const color = cfg.color || "#111";
  const textColor = cfg.textColor || "white";
  const hasLabel = !!label;
  const radius = cfg.borderRadius || (hasLabel ? "24px" : "50%");
  const offsetX = Math.max(0, cfg.offsetX ?? 20);
  const offsetY = Math.max(0, cfg.offsetY ?? 20);

  const { css: positionCss, baseTransform } = getPositionStyles(pos, offsetX, offsetY);
  button.setAttribute("data-base-transform", baseTransform);

  button.style.cssText = `
    position: fixed;
    ${positionCss}
    transform: ${baseTransform};
    ${hasLabel ? "padding: 10px 18px;" : "width: 48px; height: 48px;"}
    border-radius: ${radius};
    background: ${color};
    color: ${textColor};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0 2px 12px rgba(0,0,0,0.15);
    z-index: 99999;
    transition: transform 0.15s, box-shadow 0.15s;
  `;
}

async function createWidgetButton() {
  if (!config) return;

  // Fetch appearance from API, fall back to init options
  const remote = await fetchRemoteConfig();
  appearance = {
    color: remote?.color || config.color || "#111",
    textColor: remote?.textColor || config.textColor || "white",
    position: remote?.position || config.position || "bottom-right",
    offsetX: remote?.offsetX ?? config.offsetX ?? 20,
    offsetY: remote?.offsetY ?? config.offsetY ?? 20,
    label: remote?.label ?? config.label ?? "",
    borderRadius: remote?.borderRadius || config.borderRadius || "",
  };

  const button = document.createElement("button");
  button.id = "kodokyo-feedback-btn";
  button.title = "Send Feedback";

  applyButtonStyles(button, appearance);

  button.addEventListener("mouseenter", () => {
    const base = button.getAttribute("data-base-transform") || "";
    button.style.transform = base ? `${base} scale(1.05)` : "scale(1.05)";
    button.style.boxShadow = "0 4px 20px rgba(0,0,0,0.2)";
  });
  button.addEventListener("mouseleave", () => {
    const base = button.getAttribute("data-base-transform") || "";
    button.style.transform = base || "scale(1)";
    button.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
  });

  button.addEventListener("click", openFeedbackPanel);
  document.body.appendChild(button);
}

function openFeedbackPanel() {
  if (!config) return;

  // Hide the button
  const btn = document.getElementById("kodokyo-feedback-btn");
  if (btn) btn.style.display = "none";

  // Create overlay
  const overlay = document.createElement("div");
  overlay.id = "kodokyo-feedback-overlay";
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.3);
    z-index: 99998;
  `;

  // Create panel — position it on the same side as the button
  const pos = appearance?.position || "bottom-right";
  const offsetX = Math.max(0, appearance?.offsetX ?? 20);
  const offsetY = Math.max(0, appearance?.offsetY ?? 20);

  let panelPosCss: string;
  if (pos === "bottom-middle") panelPosCss = `left: 50%; bottom: ${offsetY}px; transform: translateX(-50%);`;
  else if (pos === "top-middle") panelPosCss = `left: 50%; top: ${offsetY}px; transform: translateX(-50%);`;
  else if (pos === "left-middle") panelPosCss = `left: ${offsetX + 60}px; top: 50%; transform: translateY(-50%);`;
  else if (pos === "right-middle") panelPosCss = `right: ${offsetX + 60}px; top: 50%; transform: translateY(-50%);`;
  else { const ph = pos.includes("left") ? "left" : "right"; const pv = pos.includes("top") ? "top" : "bottom"; panelPosCss = `${ph}: ${offsetX}px; ${pv}: ${offsetY}px;`; }

  const panel = document.createElement("div");
  panel.id = "kodokyo-feedback-panel";
  panel.style.cssText = `
    position: fixed;
    ${panelPosCss}
    width: 380px;
    max-height: 520px;
    background: white;
    color: #111;
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.12);
    z-index: 100000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    line-height: 1.5;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `;

  // Auto-fill user info if provided
  const userName = config.user?.name || "";
  const userEmail = config.user?.email || "";
  const hasUser = userName || userEmail;

  panel.innerHTML = `
    <div style="padding: 16px 20px; border-bottom: 1px solid #eee; display: flex; align-items: center; justify-content: space-between;">
      <h3 style="margin:0; font-size: 15px; font-weight: 600; color:#111;">Send Feedback</h3>
      <button id="kodokyo-close" style="background:none; border:none; cursor:pointer; padding:4px; color:#999; font-size:18px;">&times;</button>
    </div>
    <div style="padding: 16px 20px; flex:1; overflow-y:auto;">
      <div style="margin-bottom: 12px;">
        <label style="display:block; font-size:12px; font-weight:500; margin-bottom:4px; color:#555;">Title *</label>
        <input id="kodokyo-title" style="width:100%; padding:8px 10px; border:1px solid #ddd; border-radius:6px; font-size:13px; box-sizing:border-box; color:#111; background:white;" placeholder="Describe the issue..." />
      </div>
      <div style="margin-bottom: 12px;">
        <label style="display:block; font-size:12px; font-weight:500; margin-bottom:4px; color:#555;">Description</label>
        <textarea id="kodokyo-desc" style="width:100%; padding:8px 10px; border:1px solid #ddd; border-radius:6px; font-size:13px; min-height:60px; resize:vertical; box-sizing:border-box; color:#111; background:white;" placeholder="Steps to reproduce, expected behavior..."></textarea>
      </div>
      <div style="margin-bottom: 12px;">
        <label style="display:block; font-size:12px; font-weight:500; margin-bottom:4px; color:#555;">Assign to</label>
        <select id="kodokyo-assignee" style="width:100%; padding:8px 10px; border:1px solid #ddd; border-radius:6px; font-size:13px; box-sizing:border-box; color:#111; background:white;">
          <option value="">Loading team members...</option>
        </select>
      </div>
      <div style="margin-bottom: 12px;">
        <div style="display:flex; gap:6px;">
          <button id="kodokyo-screenshot-quick" style="flex:1; padding:6px 10px; border:1px solid #ddd; border-radius:6px; background:#fafafa; cursor:pointer; font-size:11px; display:flex; align-items:center; justify-content:center; gap:4px;" title="Fast DOM-based capture — no permission needed">
            ⚡ Quick Capture
          </button>
          <button id="kodokyo-screenshot-perfect" style="flex:1; padding:6px 10px; border:1px solid #ddd; border-radius:6px; background:#fafafa; cursor:pointer; font-size:11px; display:flex; align-items:center; justify-content:center; gap:4px; ${isPerfectCaptureAvailable() ? "" : "opacity:0.4; cursor:not-allowed;"}" title="Pixel-perfect screen capture — one permission click" ${isPerfectCaptureAvailable() ? "" : "disabled"}>
            📸 Perfect Capture
          </button>
        </div>
        <div id="kodokyo-preview" style="margin-top:8px;"></div>
      </div>
      ${hasUser ? `
        <div style="margin-bottom: 12px; padding: 8px 10px; background: #f9f9f9; border-radius: 6px; font-size: 12px; color: #666;">
          Reporting as <strong>${userName || userEmail}</strong>${userName && userEmail ? ` (${userEmail})` : ""}
        </div>
        <input id="kodokyo-name" type="hidden" value="${userName}" />
        <input id="kodokyo-email" type="hidden" value="${userEmail}" />
      ` : `
        <div style="margin-bottom: 12px; display: flex; gap: 8px;">
          <div style="flex:1;">
            <label style="display:block; font-size:12px; font-weight:500; margin-bottom:4px; color:#555;">Name</label>
            <input id="kodokyo-name" style="width:100%; padding:8px 10px; border:1px solid #ddd; border-radius:6px; font-size:13px; box-sizing:border-box; color:#111; background:white;" />
          </div>
          <div style="flex:1;">
            <label style="display:block; font-size:12px; font-weight:500; margin-bottom:4px; color:#555;">Email</label>
            <input id="kodokyo-email" type="email" style="width:100%; padding:8px 10px; border:1px solid #ddd; border-radius:6px; font-size:13px; box-sizing:border-box; color:#111; background:white;" />
          </div>
        </div>
      `}
    </div>
    <div style="padding: 12px 20px; border-top: 1px solid #eee;">
      <button id="kodokyo-submit" style="width:100%; padding:10px; background:#111; color:white; border:none; border-radius:8px; font-size:13px; font-weight:500; cursor:pointer;">
        Submit Feedback
      </button>
    </div>
  `;

  overlay.addEventListener("click", () => closeFeedbackPanel());
  document.body.appendChild(overlay);
  document.body.appendChild(panel);

  // Close button
  panel.querySelector("#kodokyo-close")?.addEventListener("click", closeFeedbackPanel);

  // Load team members for assignee dropdown
  fetch(`${config.apiUrl}/members?widgetKey=${config.widgetKey}`)
    .then((r) => r.json())
    .then((members: { id: string; name: string; isLead: boolean }[]) => {
      const select = panel.querySelector("#kodokyo-assignee") as HTMLSelectElement;
      if (!select || !Array.isArray(members)) return;
      select.innerHTML = "";

      // Default to project lead
      const leadId = members.find((m) => m.isLead)?.id || "";

      const defaultOpt = document.createElement("option");
      defaultOpt.value = "";
      defaultOpt.textContent = "Unassigned";
      select.appendChild(defaultOpt);

      for (const member of members) {
        const opt = document.createElement("option");
        opt.value = member.id;
        opt.textContent = member.name + (member.isLead ? " (Lead)" : "");
        select.appendChild(opt);
      }

      select.value = leadId;
    })
    .catch(() => {
      const select = panel.querySelector("#kodokyo-assignee") as HTMLSelectElement;
      if (select) {
        select.innerHTML = '<option value="">Unassigned</option>';
      }
    });

  // Screenshot buttons — two modes, shared handler
  let screenshotBlob: Blob | null = null;
  let annotationData: unknown[] | null = null;

  async function handleCapture(mode: "quick" | "perfect") {
    panel.style.display = "none";
    overlay.style.display = "none";

    try {
      const canvas = mode === "perfect"
        ? await capturePerfect()
        : await captureQuick();

      createAnnotationUI(canvas, (annotations, annotatedCanvas) => {
        annotationData = annotations;

        annotatedCanvas.toBlob((blob) => {
          screenshotBlob = blob;
          panel.style.display = "flex";
          overlay.style.display = "block";

          const preview = panel.querySelector("#kodokyo-preview") as HTMLElement;
          if (preview && blob) {
            const img = document.createElement("img");
            img.src = URL.createObjectURL(blob);
            img.style.cssText = "width:100%; border-radius:6px; border:1px solid #eee;";
            preview.innerHTML = "";
            preview.appendChild(img);

            const retakeBtn = document.createElement("button");
            retakeBtn.textContent = "Retake screenshot";
            retakeBtn.style.cssText = "margin-top:6px; padding:4px 10px; border:1px solid #ddd; border-radius:4px; background:#fafafa; cursor:pointer; font-size:11px; color:#666;";
            retakeBtn.addEventListener("click", () => {
              screenshotBlob = null;
              annotationData = null;
              preview.innerHTML = "";
            });
            preview.appendChild(retakeBtn);
          }
        }, "image/png");
      });
    } catch (err) {
      console.warn("[kodokyo] Screenshot capture failed:", err);
      panel.style.display = "flex";
      overlay.style.display = "block";

      const preview = panel.querySelector("#kodokyo-preview") as HTMLElement;
      if (preview) {
        preview.innerHTML = mode === "perfect"
          ? `<p style="color:#b91c1c; font-size:12px; margin:0;">Perfect Capture requires screen-share permission. Try Quick Capture instead.</p>`
          : `<p style="color:#b91c1c; font-size:12px; margin:0;">Screenshot capture failed. You can still submit feedback without a screenshot.</p>`;
      }
    }
  }

  panel.querySelector("#kodokyo-screenshot-quick")?.addEventListener("click", () => handleCapture("quick"));
  panel.querySelector("#kodokyo-screenshot-perfect")?.addEventListener("click", () => handleCapture("perfect"));

  // Submit button
  panel.querySelector("#kodokyo-submit")?.addEventListener("click", async () => {
    const title = (panel.querySelector("#kodokyo-title") as HTMLInputElement)?.value;
    const description = (panel.querySelector("#kodokyo-desc") as HTMLTextAreaElement)?.value;
    const name = (panel.querySelector("#kodokyo-name") as HTMLInputElement)?.value;
    const email = (panel.querySelector("#kodokyo-email") as HTMLInputElement)?.value;
    const assigneeId = (panel.querySelector("#kodokyo-assignee") as HTMLSelectElement)?.value;

    if (!title?.trim()) {
      alert("Please enter a title");
      return;
    }

    const submitBtn = panel.querySelector("#kodokyo-submit") as HTMLButtonElement;
    submitBtn.textContent = "Submitting...";
    submitBtn.disabled = true;

    try {
      const metadata = collectMetadata();
      const attachments = screenshotBlob
        ? [{
            type: annotationData?.length ? "annotated_screenshot" : "screenshot",
            fileName: "screenshot.png",
            mimeType: "image/png",
            fileSize: screenshotBlob.size,
            annotations: annotationData,
          }]
        : [];

      const result = await submitFeedback(config!.apiUrl, config!.widgetKey, {
        title: title.trim(),
        description: description || undefined,
        reporterName: name || undefined,
        reporterEmail: email || undefined,
        assigneeId: assigneeId || undefined,
        ...metadata,
        attachments,
      });

      // Upload screenshot via the server-side proxy. The feedback row is
      // already saved at this point — if the upload fails, we still consider
      // the feedback submitted but tell the user the screenshot didn't make it.
      let screenshotUploadFailed = false;
      const upload = result.uploadUrls?.[0];
      if (screenshotBlob && upload?.attachmentId) {
        if (screenshotBlob.size === 0) {
          console.error("[kodokyo] Screenshot blob is empty — not uploading.");
          screenshotUploadFailed = true;
        } else {
          try {
            await uploadAttachment(
              config!.apiUrl,
              config!.widgetKey,
              upload.attachmentId,
              screenshotBlob
            );
          } catch (err) {
            console.error("[kodokyo] Screenshot upload failed:", err);
            screenshotUploadFailed = true;
          }
        }
      }

      // Show success — note partial success if the screenshot didn't upload.
      panel.innerHTML = screenshotUploadFailed
        ? `
        <div style="padding: 40px 20px; text-align: center;">
          <div style="font-size: 32px; margin-bottom: 12px;">⚠️</div>
          <h3 style="margin: 0 0 8px; font-size: 16px; font-weight: 600;">Submitted</h3>
          <p style="margin: 0; font-size: 13px; color: #666;">Your feedback was saved, but the screenshot couldn't be uploaded.</p>
        </div>
      `
        : `
        <div style="padding: 40px 20px; text-align: center;">
          <div style="font-size: 32px; margin-bottom: 12px;">✅</div>
          <h3 style="margin: 0 0 8px; font-size: 16px; font-weight: 600;">Thank you!</h3>
          <p style="margin: 0; font-size: 13px; color: #666;">Your feedback has been submitted.</p>
        </div>
      `;

      setTimeout(closeFeedbackPanel, 2000);
    } catch {
      submitBtn.textContent = "Submit Feedback";
      submitBtn.disabled = false;
      alert("Failed to submit feedback. Please try again.");
    }
  });
}

function closeFeedbackPanel() {
  const panel = document.getElementById("kodokyo-feedback-panel");
  const overlay = document.getElementById("kodokyo-feedback-overlay");
  const btn = document.getElementById("kodokyo-feedback-btn");

  panel?.remove();
  overlay?.remove();
  if (btn) btn.style.display = "flex";
}

function init(userConfig: WidgetConfig) {
  config = userConfig;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", createWidgetButton);
  } else {
    createWidgetButton();
  }
}

// Expose globally
(window as any).KodokyoFeedback = { init };

export { init };
